import { ApostleFilterInput, ApostleSortBy } from '@apeiron/apostle'
import { recursiveToString, SortInput } from '@apeiron/common'
import useFilterQuery, {
  FilterQueryHook,
  FilterQueryProps
} from '@src/hooks/common/router/useFilterQuery'
import { extractArray, extractValue } from '@src/util/tools/query'
import { ParsedUrlQuery } from 'querystring'
import * as R from 'ramda'
import { useMemo } from 'react'

const useAposleFilterQuery = (props?: Props): Hook => {
  const filledProps = useMemo(() => {
    const supportUrl = R.propOr(true, 'supportUrl', props) as boolean

    const toFilter = (query: ParsedUrlQuery): ApostleFilterInput => {
      return {
        activityType: extractArray(query, 'activityType'),
        apostleID: recursiveToString(extractArray(query, 'apostleID')),
        class: recursiveToString(extractArray(query, 'class')),
        ivMax: extractValue<number>(query, 'ivMax'),
        ivMin: extractValue<number>(query, 'ivMin'),
        nftListingStatus: recursiveToString(
          extractArray(query, 'nftListingStatus')
        ),
        ownerWalletAddresses: recursiveToString(
          extractArray(query, 'ownerWalletAddresses')
        ),
        reforgeCountMax: extractValue<number>(query, 'reforgeCountMax'),
        reforgeCountMin: extractValue<number>(query, 'reforgeCountMin'),
        firstSkillIDs: recursiveToString(extractArray(query, 'firstSkillIDs')),
        otherSkillIDs: recursiveToString(extractArray(query, 'otherSkillIDs')),
        skillLevelMax: extractValue<number>(query, 'skillLevelMax'),
        skillLevelMin: extractValue<number>(query, 'skillLevelMin'),
        ticketMint: recursiveToString(extractArray(query, 'ticketMint'))
      }
    }

    const toSort = (query: ParsedUrlQuery): SortInput<ApostleSortBy> => {
      return {
        sortBy: R.propOr(
          props?.defaultSortBy ?? ApostleSortBy.PriceAsc,
          'sortBy',
          query
        )
      }
    }

    return {
      supportUrl,
      queryToFilterInput: toFilter,
      queryToSortInput: toSort,
      excludedSearchParams: ['page']
    }
  }, [props])

  const {
    filterInput,
    isReady,
    sortInput,
    setBothInput,
    setFilterInput,
    setSortInput
  } = useFilterQuery(filledProps)

  return {
    filterInput,
    isReady,
    sortInput,
    setBothInput,
    setFilterInput,
    setSortInput
  }
}

type Props = Omit<
  FilterQueryProps<ApostleFilterInput, ApostleSortBy>,
  'queryToFilterInput' | 'queryToSortInput'
> & { defaultSortBy?: ApostleSortBy }

type Hook = FilterQueryHook<ApostleFilterInput, ApostleSortBy>

export default useAposleFilterQuery
